// ---------- platform layer ----------
// The rest of the code doesn't know whether it's running in a browser or in the
// Capacitor WebView: it calls `buzz()` and `initPlatform()`, this layer picks the
// implementation.

import { App } from '@capacitor/app';
import { Share } from '@capacitor/share';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';
import { skinById } from './skin';
import { S } from './state';

const nativePlatform = Capacitor.isNativePlatform();

export type Intensity = 'light' | 'medium' | 'mistake';

/**
 * Haptic feedback. On native we go through Haptics (calibrated by the OS, far better
 * than a duration in milliseconds); on the web we fall back to navigator.vibrate,
 * silently ignored where it doesn't exist.
 */
export function buzz(kind: Intensity): void {
  if (!S.haptics) return; // switched off in the settings: silence the whole layer at the source
  if (nativePlatform) {
    if (kind === 'mistake') void Haptics.notification();
    else
      void Haptics.impact({
        style: kind === 'medium' ? ImpactStyle.Medium : ImpactStyle.Light,
      });
    return;
  }
  const ms = kind === 'mistake' ? 45 : kind === 'medium' ? 22 : 12;
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    try {
      navigator.vibrate(ms);
    } catch {
      /* nothing to do, it's decorative */
    }
  }
}

export function isNative(): boolean {
  return nativePlatform;
}

/**
 * Settings that only make sense natively: status bar tint, and the hardware back
 * button — without intervention it would close the app outright, which would lose
 * the game.
 */
/**
 * Ouvre le sélecteur de partage du système, ou à défaut copie le lien.
 *
 * Trois chemins, et un seul plugin. Sur l'APK, `navigator.share` N'EXISTE PAS — vérifié sur
 * l'appareil, la WebView de Capacitor ne l'expose pas — d'où @capacitor/share, dont le manifeste
 * Android est vide : aucune permission ajoutée. Sur le web, `navigator.share` est là et fait le
 * même travail sans plugin. Partout ailleurs — un navigateur de bureau — il reste le
 * presse-papiers, qui est un partage lent mais un partage.
 *
 * On ne joint PAS de fichier, et c'est ce qui garde tout ça léger : `Share.share({ files })`
 * exigerait d'écrire l'image sur le disque, donc un second plugin. On partage une URL, et c'est
 * la page au bout du lien qui redessine le dessin en grand (cf. src/share.ts).
 */
export async function shareSheet(o: {
  title: string;
  text: string;
  url: string;
}): Promise<'partagé' | 'copié' | 'échec'> {
  try {
    if (nativePlatform) {
      await Share.share(o);
      return 'partagé';
    }
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (nav.share) {
      await nav.share({ title: o.title, text: o.text, url: o.url });
      return 'partagé';
    }
    await navigator.clipboard.writeText(`${o.text}\n${o.url}`);
    return 'copié';
  } catch {
    // Un partage annulé par le joueur passe aussi par ici, et c'est bien : il n'y a rien à lui
    // dire d'une action qu'il vient d'abandonner.
    return 'échec';
  }
}

/**
 * Partager un FICHIER — l'image d'une création terminée, ou la vidéo du remplissage d'une feuille
 * (cf. src/sharePanel.ts). Un fichier et non un lien : une création est privée, aucune adresse ne
 * la sert à personne (cf. backend/src/routes/creations.ts). Le joueur choisit lui-même à qui
 * l'envoyer, comme une photo, et rien ne passe par le serveur.
 *
 * Le partage de fichiers du navigateur (`navigator.share({ files })`) d'abord ; à défaut — un
 * ordinateur, ou une page en http qui n'y a pas droit —, l'image est TÉLÉCHARGÉE, ce qui laisse au
 * joueur le soin de l'envoyer.
 *
 * Dans l'application, la WebView n'a ni l'un ni l'autre. Le fichier est écrit dans le cache par
 * CacheFilePlugin (android/…/CacheFilePlugin.java, un plugin à nous : une seule méthode, et
 * l'installation de @capacitor/filesystem pour elle seule ne valait pas une dépendance), puis
 * confié à @capacitor/share, qui sait joindre un file:// par le FileProvider déjà déclaré.
 * Jusqu'au 2026-10-18 ce chemin n'existait pas : le bouton Partager d'une création était caché
 * dans l'application, et celui d'une feuille du catalogue n'y envoyait que le lien.
 */
export function canShareFile(): boolean {
  // Dans l'application, seulement si le plugin y est : une enveloppe native qui ne l'enregistre
  // pas (un futur iOS, un APK d'avant) retombe sur le partage du lien plutôt que sur un bouton
  // « Envoyer » muet (revue du 2026-10-18).
  return !nativePlatform || Capacitor.isPluginAvailable('CacheFile');
}

const CacheFile = registerPlugin<{
  write(o: { name: string; data: string }): Promise<{ uri: string }>;
}>('CacheFile');

/** Le blob en base64 nu, ce que le pont de Capacitor transporte. FileReader plutôt qu'une boucle
 *  sur les octets : une vidéo de quelques Mo y passe sans bloquer le fil. */
function base64Of(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

export async function shareFile(
  blob: Blob,
  name: string,
  text: string,
): Promise<'partagé' | 'téléchargé' | 'échec'> {
  try {
    if (nativePlatform) {
      const { uri } = await CacheFile.write({ name, data: await base64Of(blob) });
      await Share.share({ files: [uri], text });
      return 'partagé';
    }
    const file = new File([blob], name, { type: blob.type || 'application/octet-stream' });
    const nav = navigator as Navigator & {
      canShare?: (d: ShareData) => boolean;
      share?: (d: ShareData) => Promise<void>;
    };
    if (nav.share && nav.canShare?.({ files: [file] })) {
      await nav.share({ files: [file], text });
      return 'partagé';
    }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    return 'téléchargé';
  } catch {
    // Annulé par le joueur, ou refusé par le navigateur : rien à lui dire.
    return 'échec';
  }
}

/**
 * La barre système, dans les couleurs du thème courant. Appelée par settings.ts à chaque
 * changement de thème, et une fois par initPlatform() au démarrage.
 *
 * Le thème est lu sur <html> et non reçu en paramètre : l'attribut y est posé par le script en
 * tête de index.html avant la première image, donc il est exact dès le premier appel — et
 * surtout, importer settings.ts ici créerait un cycle, puisque c'est lui qui appelle.
 *
 * `chrome` et non `var(--bg)` : la couleur doit être un #RRGGBB pour le plugin, là où le jeton
 * de la charte se calcule en `rgb(...)`.
 */
export async function applyChrome(): Promise<void> {
  if (!nativePlatform) return;
  const skin = skinById(document.documentElement.dataset['skin'] ?? '');
  try {
    // Style.Light = texte SOMBRE sur la barre (pour un fond clair), Style.Dark = texte clair.
    // La nomenclature du plugin nomme le thème du contenu, pas celui du fond.
    await StatusBar.setStyle({ style: skin.id === 'soir' ? Style.Dark : Style.Light });
    await StatusBar.setBackgroundColor({ color: skin.chrome });
  } catch {
    /* pas de barre à habiller : rien à réparer */
  }
}

export async function initPlatform(onBack: () => boolean): Promise<void> {
  if (!nativePlatform) return;
  try {
    await applyChrome();
    // Puis on la cache. Elle coûtait 45 px de hauteur utile sur un écran de 914 — 5 % de la
    // feuille, mesuré sur le Pixel 7a — pour une heure et une jauge de batterie que le joueur
    // a déjà vues avant d'ouvrir le jeu. La WebView était DÉJÀ en pleine hauteur (Capacitor
    // impose l'edge-to-edge) : ces 45 px étaient réservés par env(safe-area-inset-top), pas
    // occupés par le système, donc les rendre ne déplace rien d'autre.
    //
    // `hide()` et pas un mode immersif maison : le comportement d'Android reste celui qu'on
    // attend — un glissement depuis le haut la ramène le temps de la consulter, puis elle
    // repart. C'est pour ce moment-là que le style et la couleur ci-dessus restent posés.
    //
    // Le style ET la couleur d'abord, la disparition ensuite : l'ordre inverse laissait la
    // barre se rhabiller en clignotant au moment où elle est rappelée.
    await StatusBar.hide();
    // ET on cesse de réserver sa bande. Sans cette ligne, la cacher ne gagnait RIEN : mesuré
    // sur le Pixel 7a, env(safe-area-inset-top) continue d'annoncer 45 px même barre cachée,
    // donc on remplaçait l'heure par un aplat mûre vide. Avec elle, la feuille passe de 576 à
    // 606 px de haut — 5 % de plus à peindre.
    //
    // Un attribut et non un style en dur : la correspondance reste dans la feuille de style
    // (cf. :root[data-nobar]), et le drapeau n'est posé que si hide() a réellement abouti —
    // dans un navigateur, où initPlatform() sort tout de suite, la bande reste réservée parce
    // que la barre y est celle du système et non la nôtre.
    document.documentElement.dataset['nobar'] = '';
  } catch {
    /* some surfaces don't expose it */
  }
  await App.addListener('backButton', () => {
    // onBack returns true if it consumed the gesture (closing a panel, a banner...)
    if (!onBack()) void App.minimizeApp();
  });
}

/**
 * Catch-up for the installed web app, whose manifest asks for `display: fullscreen` — no
 * status bar, no gesture bar, the sheet gets the whole screen.
 *
 * The manifest alone doesn't cover every icon: the display mode is frozen when the app is
 * installed, so anyone who added Codigrid to their home screen before this change keeps
 * running in `standalone`. The Fullscreen API is the only way back, and it demands a user
 * gesture — hence the wait for the first tap rather than a call at startup.
 *
 * Deliberately not done in a browser tab: there, full screen swallows the address bar, i.e.
 * the way out of a page the player did not ask to be locked into.
 */
export function initFullscreen(): void {
  if (nativePlatform) return;
  const el = document.documentElement;
  // Absent on iOS, where only a <video> may go full screen. Nothing to do there: the app
  // already runs under the status bar thanks to black-translucent.
  if (!el.requestFullscreen) return;
  if (!matchMedia('(display-mode: standalone), (display-mode: minimal-ui)').matches) return;

  // Capture, and pointerdown rather than click: the canvas swallows touch gestures, and a tap
  // on the sheet — the most likely first tap — never becomes a click.
  window.addEventListener(
    'pointerdown',
    () => {
      void el.requestFullscreen({ navigationUI: 'hide' }).catch(() => {
        /* refused (policy, unsupported): we stay in standalone, which is playable */
      });
    },
    { once: true, capture: true },
  );
}
